/**
 * v0.11.0 AppShellV2 — TopBar + main + BottomDock for ALL screen sizes.
 *
 * Per user (2026-05-02): the old desktop LeftRail fallback is dropped —
 * the new design system applies everywhere, no width-based branching.
 * The legacy `<AppShell>` import is kept available temporarily in case
 * we need to roll back during PR-4 page migration; once all pages are
 * verified we can delete `components/AppShell.tsx`.
 *
 * Bond level for TopBar pill is fetched per active pet on mount + on
 * 'reward:burst' so it stays in sync with the existing BondBadge.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
// PR-2.7 — TopBar import dropped; ParkPage owns its own inline TopBar.
import { BottomDock } from './BottomDock';
import CoinFly from '../CoinFly';
import { api } from '../../api/client';
import type { AppContext } from '../../App';
import {
  classifyCue,
  getMusicEnabled,
  getMusicStyle,
  getMusicStyleOptions,
  playCue,
  preloadFeedbackVoices,
  preloadUiSounds,
  setMusicEnabled,
  setMusicStyle,
  unlockAudio,
  type MusicStyle,
} from '../../lib/gameAudio';
import { t, useLang } from '../../i18n';

const SHELL_MAX_WIDTH = '1180px';
const SPECIES_EMOJI: Record<string, string> = {
  hamster: '🐹',
  rabbit: '🐰',
  dog: '🐕',
  dragon: '🐉',
  parrot: '🦜',
  axolotl: '🦎',
  hedgehog: '🦔',
  capybara: '🦫',
  otter: '🦦',
  koala: '🐨',
  cat: '🐱',
  snake: '🐍',
  turtle: '🐢',
  fish: '🐠',
  frog: '🐸',
};

interface AppShellV2Props {
  ctx: AppContext;
  children: ReactNode;
}

export function AppShellV2({ ctx, children }: AppShellV2Props) {
  useLang();
  const location = useLocation();
  // v0.14.0 PR-2.7 — TopBar moved into ParkPage as an inline header.
  // bondLevel logic moved into TopBar itself (self-fetches now).
  const [unreadChat, setUnreadChat] = useState(false);
  const [chatAlerting, setChatAlerting] = useState(false);
  const [musicMenuOpen, setMusicMenuOpen] = useState(false);
  const [musicEnabled, setMusicEnabledState] = useState(() => getMusicEnabled());
  const [musicStyle, setMusicStyleState] = useState<MusicStyle>(() => getMusicStyle());
  const showCompanionTools = !!ctx.activePet && location.pathname !== '/chat';
  const hasConversationUnread = unreadChat || ctx.mailUnread > 0;
  const petEmoji = ctx.activePet
    ? (SPECIES_EMOJI[ctx.activePet.species] || ctx.child.avatar || '🐾')
    : (ctx.child.avatar || '🐾');

  useEffect(() => {
    const onPointerDown = (event: PointerEvent) => {
      void unlockAudio();
      preloadUiSounds();
      preloadFeedbackVoices();
      const cue = classifyCue(event.target);
      if (cue) playCue(cue);
    };
    document.addEventListener('pointerdown', onPointerDown, { capture: true });
    return () => document.removeEventListener('pointerdown', onPointerDown, { capture: true });
  }, []);

  useEffect(() => {
    const sync = () => {
      setMusicEnabledState(getMusicEnabled());
      setMusicStyleState(getMusicStyle());
    };
    window.addEventListener('xiaopeiban:music-toggle', sync);
    window.addEventListener('xiaopeiban:music-style', sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener('xiaopeiban:music-toggle', sync);
      window.removeEventListener('xiaopeiban:music-style', sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  useEffect(() => {
    setMusicMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!ctx.activePet) {
      setUnreadChat(false);
      return;
    }

    const seenKey = 'chat_last_seen_at';
    const notifiedKey = 'chat_last_notified_at';
    let alive = true;
    let alertTimer: number | null = null;

    function notifyUnreadMessage(messageCreatedAt: number) {
      if (location.pathname === '/chat') return;
      const lastNotifiedAt = Number(window.localStorage.getItem(notifiedKey) || 0);
      if (messageCreatedAt <= lastNotifiedAt) return;
      window.localStorage.setItem(notifiedKey, String(messageCreatedAt));
      playCue('message');
      if ('vibrate' in navigator) navigator.vibrate([70, 35, 110]);
      setChatAlerting(true);
      if (alertTimer) window.clearTimeout(alertTimer);
      alertTimer = window.setTimeout(() => {
        if (alive) setChatAlerting(false);
      }, 1400);
    }

    async function refreshUnread() {
      try {
        const r = await api.chatHistory(20);
        if (!alive) return;
        const lastAsst = [...r.messages].reverse().find((m) => m.role === 'assistant');
        if (!lastAsst) {
          setUnreadChat(false);
          return;
        }
        const seenAt = Number(window.localStorage.getItem(seenKey) || 0);
        const hasUnread = lastAsst.created_at > seenAt;
        setUnreadChat(hasUnread);
        if (hasUnread) notifyUnreadMessage(lastAsst.created_at);
      } catch {
        if (alive) setUnreadChat(false);
      }
    }

    void refreshUnread();
    const timer = window.setInterval(refreshUnread, 60_000);
    return () => {
      alive = false;
      window.clearInterval(timer);
      if (alertTimer) window.clearTimeout(alertTimer);
    };
  }, [ctx.activePet?.id, location.pathname]);

  // v0.14.0 PR-2.7 — TopBar no longer rendered at shell level. ParkPage
  // embeds it inline as page content; other pages render no top bar at
  // all and their content sits flush under the safe-area inset.
  return (
    <div
      className="xpb-shell xpb-shell--no-topbar"
      style={{
        ['--xpb-shell-max-width' as string]: SHELL_MAX_WIDTH,
      }}
    >
      <main className="xpb-shell-main">
        {children}
      </main>
      {showCompanionTools && ctx.activePet && (
        <div className={`companion-tools ${musicMenuOpen ? 'is-music-open' : ''}`}>
          {musicMenuOpen && (
            <div className="companion-music-menu" role="menu">
              <div className="companion-music-menu-title">{t('music.title')}</div>
              <button
                type="button"
                role="menuitemcheckbox"
                aria-checked={musicEnabled}
                className={`companion-music-option companion-music-power ${musicEnabled ? 'active' : ''}`}
                onClick={() => {
                  const next = !musicEnabled;
                  setMusicEnabledState(next);
                  setMusicEnabled(next);
                  playCue(next ? 'success' : 'tap');
                }}
              >
                <span className="companion-music-option-emoji" aria-hidden>{musicEnabled ? '🔊' : '🔇'}</span>
                <span className="companion-music-option-text">
                  <b>{musicEnabled ? t('music.power.on') : t('music.power.off')}</b>
                  <small>{musicEnabled ? t('music.power.helper.on') : t('music.power.helper.off')}</small>
                </span>
              </button>
              {getMusicStyleOptions().map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  role="menuitemradio"
                  aria-checked={musicStyle === opt.value}
                  disabled={!musicEnabled}
                  className={`companion-music-option ${musicStyle === opt.value ? 'active' : ''}`}
                  onClick={() => {
                    setMusicStyle(opt.value);
                    setMusicStyleState(opt.value);
                    setMusicMenuOpen(false);
                    playCue('success');
                  }}
                >
                  <span className="companion-music-option-emoji" aria-hidden>{opt.emoji}</span>
                  <span className="companion-music-option-text">
                    <b>{opt.label}</b>
                    <small>{opt.helper}</small>
                  </span>
                  {musicStyle === opt.value && <span className="companion-music-option-check" aria-hidden>✓</span>}
                </button>
              ))}
              <div className="companion-music-menu-foot">{t('music.foot.scene')}</div>
            </div>
          )}
          <button
            type="button"
            className={`companion-music-button ${musicEnabled ? '' : 'muted'}`}
            onClick={() => {
              setMusicMenuOpen((v) => !v);
              playCue('tap');
            }}
            aria-label={t('music.aria')}
            title={musicEnabled ? t('music.tooltip.on', { style: musicStyle }) : t('music.tooltip.off')}
          >
            <span aria-hidden>
              {musicEnabled
                ? (getMusicStyleOptions().find((o) => o.value === musicStyle)?.emoji || '🎵')
                : '🔇'}
            </span>
          </button>
          <Link
            to="/chat"
            className={`companion-chat-button ${hasConversationUnread ? 'has-unread' : ''} ${chatAlerting ? 'is-alerting' : ''}`}
            aria-label={hasConversationUnread
              ? t('chat.fab.unread.aria', { name: ctx.activePet.nickname })
              : t('chat.fab.idle.aria', { name: ctx.activePet.nickname })}
          >
            <span className="companion-chat-avatar" aria-hidden>{petEmoji}</span>
            <span className="companion-chat-copy">
              <b>{hasConversationUnread ? t('chat.fab.unread.label') : t('chat.fab.idle.label')}</b>
              <small>{ctx.activePet.nickname}</small>
            </span>
            <span className="companion-chat-bubble" aria-hidden>💬</span>
            {hasConversationUnread && <span className="companion-chat-dot" aria-hidden />}
          </Link>
        </div>
      )}
      <BottomDock ctx={ctx} />
      <CoinFly />
    </div>
  );
}
